import {ISimplePresentation, ISimplePresentationRunnerController} from "../SimplePresentationRunner";
import {Game} from "../Game";
import {gsap} from "gsap";
import {IMonster, IMonsterWalk, IPosition, MONSTER_EXPRESSION} from "../server/IData";
import {Symbol} from "../reels/Symbol";
import {MonsterSymbol} from "../reels/MonsterSymbol";
import {Sound} from "../sound/Sound";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";

export class BazookaRunner implements ISimplePresentation {

    public shouldRun(): boolean {
        return Game.model.gameData.bazookaPosition && Game.model.gameData.bazookaPosition.x !== -1 && !Game.model.gameData.suspendedAction;
    }

    public start(controller: ISimplePresentationRunnerController): void {
        const tl: gsap.core.Timeline = gsap.timeline({
                onComplete: () => {
                    controller.finish();
                }
            }),
            monsters: IMonster[] = Game.model.gameData.monsters,
            bazookaTarget: IMonsterWalk = Game.model.gameData.bazookaPosition,
            maxExpansion: boolean = Game.model.gameData.maxExpansion,
            expansionDirection: number[] = Game.model.gameData.expansionDirections[0],
            carriedSymbolUserNode: IPosition = Game.model.gameData.carriedSymbolUserNode,
            turnDuration: number = Game.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0.2 / 2 : 0.2;

        let extraReels: number = 0,
            extraRows: number = 0,
            addReelsToRight: boolean = false,
            addRowsInBottom: boolean = false;

        // if maxExpansion then length of expansionDirections is 5, which creates issues => #132
        if (maxExpansion) {
            // #130 => intentionally want to modify this array in place.
            Game.model.gameData.expansionDirections.shift();
        }

        tl.add(() => {
            controller.setSkipCallback(() => {
                this.onSkip(tl);
            });
        });

        //let posForExplode: string = "<+2.266";
        let posForExplode: string = "<+1.2";

        if (!Game.model.gameData.expansionDirections.length) {
            const directionData = this.getBazookaHitDirection(bazookaTarget);

            for (let ctr: number = 0; ctr < monsters.length; ctr++) {
                const monster: IMonster = monsters[ctr];
                if (!monster.path || monster.path.length === 0) {
                    continue;
                }

                if (monster.path[0].x === carriedSymbolUserNode.x && monster.path[0].y === carriedSymbolUserNode.y) {
                    const monsterSymbol: MonsterSymbol = Game.reelsView.reelModels[monster.path[0].x].getMonsterSymbolAt(monster.symbol);
                    tl.add([
                        () => {
                            if (monsterSymbol && monsterSymbol.parent) {
                                monsterSymbol.parent.addChild(monsterSymbol);
                            }
                        },
                        monsterSymbol.turnMonsterTowardsTarget(monster.path[0].x, bazookaTarget.x, turnDuration)
                    ]);
                    tl.add(monsterSymbol.throwBazooka(bazookaTarget));
                    tl.add(this.showBZExplodePattern(bazookaTarget, directionData.addReelsToRight, directionData.addRowsInBottom), "<");
                    tl.add(this.handleMonstersExpression(bazookaTarget, directionData.addReelsToRight, directionData.addRowsInBottom), "<+1.1");

                    // #337
                    if (!this.checkIfTriggeringMonsterExploding(bazookaTarget, directionData.extraReels, directionData.extraRows, directionData.addReelsToRight, directionData.addRowsInBottom, monsterSymbol.name)) {
                        tl.add([
                            monsterSymbol.getStateAnimation(MONSTER_EXPRESSION.LAUGH, 5),
                            () => {
                                Game.soundPlayer.playSound(monsterSymbol.name + "Laugh" + MathHelper.randomIntInRange(1, 3), 1);
                            }
                        ], posForExplode);
                        posForExplode = "<";
                    }

                    break;
                }
            }

            tl.add(this.handleExplosions(bazookaTarget, directionData.extraReels, directionData.extraRows, directionData.addReelsToRight, directionData.addRowsInBottom, maxExpansion, false), posForExplode);
        } else {
            extraReels = (expansionDirection[0] === 0 ? 0 : (Game.ACTIVE_REELS_NUM === 8 ? 1 : 2));
            extraRows = (expansionDirection[1] === 0 ? 0 : (Game.ACTIVE_ROWS_NUM === 8 ? 1 : 2));
            addReelsToRight = (extraReels > 0 && expansionDirection[0] === 1);
            addRowsInBottom = (extraRows > 0 && expansionDirection[1] === 1);

            for (let ctr: number = 0; ctr < monsters.length; ctr++) {
                const monster: IMonster = monsters[ctr];
                if (!monster.path || (monster.path && monster.path.length === 0)) {
                    continue;
                }

                if (monster.path[0].x === carriedSymbolUserNode.x && monster.path[0].y === carriedSymbolUserNode.y) {
                    const monsterSymbol: MonsterSymbol = Game.reelsView.reelModels[monster.path[0].x].getMonsterSymbolAt(monster.symbol);

                    tl.add(monsterSymbol.turnMonsterTowardsTarget(monster.path[0].x, bazookaTarget.x, turnDuration));
                    tl.add(monsterSymbol.throwBazooka(bazookaTarget));
                    tl.add(this.showBZExplodePattern(bazookaTarget, addReelsToRight, addRowsInBottom), "<");
                    tl.add(this.handleMonstersExpression(bazookaTarget, addReelsToRight, addRowsInBottom), "<+1.1");

                    // #337
                    if (!this.checkIfTriggeringMonsterExploding(bazookaTarget, extraReels, extraRows, addReelsToRight, addRowsInBottom, monsterSymbol.name)) {
                        tl.add([
                            monsterSymbol.getStateAnimation(MONSTER_EXPRESSION.LAUGH, 5),
                            () => {
                                Game.soundPlayer.playSound(monsterSymbol.name + "Laugh" + MathHelper.randomIntInRange(1, 3), 1);
                            }
                        ], posForExplode);
                        posForExplode = "<";
                    }

                    break;
                }
            }
            tl.add(this.handleExplosions(bazookaTarget, extraReels, extraRows, addReelsToRight, addRowsInBottom, maxExpansion, false), posForExplode);
            if (!maxExpansion) {
                const prevReelsNum: number = Game.ACTIVE_REELS_NUM, prevRowsNum: number = Game.ACTIVE_ROWS_NUM;
                tl.add(Game.expandGrid(extraReels, extraRows, addReelsToRight, addRowsInBottom), "<"); //<+2.266
                tl.add(this.showBZExplodePatternAfterExpansion(bazookaTarget, addReelsToRight, addRowsInBottom, prevReelsNum, prevRowsNum), "<");
            }
        }
        
        if (maxExpansion){
            tl.add(Game.explodeSymbolsBeforeMaxExpansion(), "<0.5");
            tl.add(Game.expandGridForMaxExpansion(), "<0.55");
        }

        tl.add(() => {
            Game.shakeScreen()
        }, "-=0.8");
    }

    private getBazookaHitDirection(bazookaTarget: IMonsterWalk): {
        extraReels: number,
        extraRows: number,
        addReelsToRight: boolean,
        addRowsInBottom: boolean
    } {
        const targetReelId: number = bazookaTarget.x,
            targetRowId: number = bazookaTarget.y;
        const isTopEdgeHit: boolean = targetRowId === 0;
        const isBottomEdgeHit: boolean = targetRowId === Game.ACTIVE_ROWS_NUM - 1;
        const isLeftEdgeHit: boolean = targetReelId === 0;
        const isRightEdgeHit: boolean = targetReelId === Game.ACTIVE_REELS_NUM - 1;
        let addReelsToRight: boolean = false, addRowsInBottom: boolean = false, extraReels: number = 0,
            extraRows: number = 0;

        if (isTopEdgeHit || isBottomEdgeHit) {
            addRowsInBottom = isBottomEdgeHit;
            extraRows = 2;
        } else if (isLeftEdgeHit || isRightEdgeHit) {
            addReelsToRight = isRightEdgeHit;
            extraReels = 2;
        }

        return {extraReels, extraRows, addReelsToRight, addRowsInBottom};
    }

    private showBZExplodePattern(bazookaTarget: IMonsterWalk, addReelsToRight: boolean, addRowsInBottom: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            targetReelId: number = bazookaTarget.x,
            targetRowId: number = bazookaTarget.y;

        tl.add(() => {
            Game.soundPlayer.playSound(Sound.ATTACK_HIGHLIGHT, 1);
        });

        if (targetReelId === 0 || targetReelId === Game.ACTIVE_REELS_NUM - 1) {
            let ctr: number = 3,
                reelId: number = targetReelId;

            while (ctr > -1) {
                tl.add([
                    Game.showExplodePatternHighlighterOnSymbols(reelId, targetRowId - ctr, targetRowId),
                    Game.showExplodePatternHighlighterOnSymbols(reelId, targetRowId, targetRowId + ctr + 1)
                ], 0);

                ctr--;
                if (addReelsToRight) {
                    reelId--;
                } else {
                    reelId++;
                }
            }
        } else if (targetRowId === 0 || targetRowId === Game.ACTIVE_ROWS_NUM - 1) {
            let ctr: number = 3,
                rowId: number = targetRowId;

            while (ctr > -1) {
                for (let i: number = targetReelId - ctr; i < targetReelId; i++) {
                    tl.add(Game.showExplodePatternHighlighterOnSymbols(i, rowId, rowId + 1), 0);
                }
                for (let i: number = targetReelId; i <= targetReelId + ctr; i++) {
                    tl.add(Game.showExplodePatternHighlighterOnSymbols(i, rowId, rowId + 1), 0);
                }
                ctr--;
                if (addRowsInBottom) {
                    rowId--;
                } else {
                    rowId++
                }
            }
        }

        return tl;
    }

    private showBZExplodePatternAfterExpansion(bazookaTarget: IMonsterWalk, addReelsToRight: boolean, addRowsInBottom: boolean, prevReelsNum: number, prevRowsNum: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            targetReelId: number = bazookaTarget.x,
            targetRowId: number = bazookaTarget.y;

        tl.add(() => {
            Game.soundPlayer.playSound(Sound.ATTACK_HIGHLIGHT, 1);
        });

        if (targetReelId === 0 || targetReelId === prevReelsNum - 1) {
            let ctr: number = 2,
                reelId = addReelsToRight ? targetReelId + 1 : 1;

            if (prevReelsNum !== Game.TOTAL_REELS_NUM) {
                if (ctr === 2) {
                    if (addReelsToRight) {
                        tl.add([
                            Game.showExplodePatternHighlighterOnSymbols(prevReelsNum - 1, 0, 0),
                            Game.showExplodePatternHighlighterOnSymbols(prevReelsNum - 1, Game.ACTIVE_ROWS_NUM - 1, Game.ACTIVE_ROWS_NUM - 1),
                            // Game.showExplodePatternSparkOnSymbols(prevReelsNum-1, 0, 0),
                            Game.showExplodePatternSparkOnSymbols(prevReelsNum - 1, Game.ACTIVE_ROWS_NUM - 3, Game.ACTIVE_ROWS_NUM - 1)
                        ], 0);
                    } else {
                        tl.add([
                            Game.showExplodePatternHighlighterOnSymbols(2, 0, 0),
                            Game.showExplodePatternHighlighterOnSymbols(2, Game.ACTIVE_ROWS_NUM - 1, Game.ACTIVE_ROWS_NUM - 1),
                            Game.showExplodePatternSparkOnSymbols(2, 0, 0),
                            Game.showExplodePatternSparkOnSymbols(2, Game.ACTIVE_ROWS_NUM - 3, Game.ACTIVE_ROWS_NUM)
                        ], 0);
                    }
                }
                while (ctr > 0) {
                    tl.add([
                        Game.showExplodePatternHighlighterOnSymbols(reelId, targetRowId - ctr, targetRowId + 1),
                        Game.showExplodePatternHighlighterOnSymbols(reelId, targetRowId + 1, targetRowId + ctr + 2),
                        Game.showExplodePatternSparkOnSymbols(reelId, targetRowId - ctr, targetRowId + 1),
                        Game.showExplodePatternSparkOnSymbols(reelId, targetRowId + 1, targetRowId + ctr + 1),
                    ], 0);

                    ctr--;
                    if (addReelsToRight) {
                        reelId++;
                    } else {
                        reelId--;
                    }
                }
            }
        } else if (targetRowId === 0 || targetRowId === prevRowsNum - 1) {
            let ctr: number = 2,
                rowId = addRowsInBottom ? targetRowId + 1 : 1;

            if (prevRowsNum !== Game.TOTAL_REELS_NUM) {
                if (ctr === 2) {
                    if (addRowsInBottom) {
                        tl.add([
                            Game.showExplodePatternHighlighterOnSymbols(0, Game.ACTIVE_ROWS_NUM - 1, Game.ACTIVE_ROWS_NUM - 1),
                            Game.showExplodePatternHighlighterOnSymbols(prevReelsNum - 1, Game.ACTIVE_ROWS_NUM - 1, Game.ACTIVE_ROWS_NUM - 1),
                            Game.showExplodePatternSparkOnSymbols(0, Game.ACTIVE_ROWS_NUM - 1, Game.ACTIVE_ROWS_NUM - 1),
                            Game.showExplodePatternSparkOnSymbols(prevReelsNum - 1, Game.ACTIVE_ROWS_NUM - 1, Game.ACTIVE_ROWS_NUM - 1)
                        ], 0);
                    } else {
                        tl.add([
                            Game.showExplodePatternHighlighterOnSymbols(0, 0, 0),
                            Game.showExplodePatternHighlighterOnSymbols(prevReelsNum, 0, 0),
                            Game.showExplodePatternSparkOnSymbols(0, 0, 0),
                            Game.showExplodePatternSparkOnSymbols(prevReelsNum, 0, 0),
                        ], 0);
                    }
                }

                while (ctr > -1) {
                    for (let i: number = targetReelId - ctr; i < targetReelId; i++) {
                        tl.add(Game.showExplodePatternHighlighterOnSymbols(i, rowId, rowId + 1), 0);
                        tl.add(Game.showExplodePatternSparkOnSymbols(i, rowId, rowId + 1), 0);
                    }
                    for (let i: number = targetReelId; i <= targetReelId + ctr; i++) {
                        tl.add(Game.showExplodePatternHighlighterOnSymbols(i, rowId, rowId + 1), 0);
                        tl.add(Game.showExplodePatternSparkOnSymbols(i, rowId, rowId + 1), 0);
                    }
                    ctr--;
                    if (addRowsInBottom) {
                        rowId++;
                    } else {
                        rowId--;
                    }
                }
            }
        }

        return tl;
    }

    private handleExplosions(bazookaTarget: IMonsterWalk, extraReels: number, extraRows: number, addReelsToRight: boolean, addRowsInBottom: boolean, maxExpansion: boolean, shouldPlayExpression: boolean = true): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            targetReelId: number = bazookaTarget.x,
            targetRowId: number = bazookaTarget.y,
            reelsSymName: string = Game.model.gameData.evaluatedArea[targetReelId][targetRowId],
            bzSymbol: Symbol = Game.reelsView.reelModels[targetReelId].getSymbolAt(targetRowId, reelsSymName);

        // #337
        if (shouldPlayExpression) {
            tl.add(this.handleMonstersExpression(bazookaTarget, addReelsToRight, addRowsInBottom), 0);
        }
        tl.addLabel("explode");
        tl.add(Game.symbolFloors.showExplosionMark(bazookaTarget.x, bazookaTarget.y), "<0.2");

        // #451, #463
        if (bzSymbol) {
            tl.add([
                () => {
                    Game.soundPlayer.playSound(Sound.BZ_EXPLODE, 1);
                },
                bzSymbol.playBZBigExplode(bzSymbol)
            ], "explode");
        }

        if (extraReels > 0) {
            let ctr: number = 3,
                reelId: number = targetReelId;

            while (ctr > -1) {
                tl.add([
                    Game.explodeSymbols(reelId, targetRowId - ctr, targetRowId + 1, true, false),
                    Game.explodeSymbols(reelId, targetRowId, targetRowId + ctr + 1, true, false)
                ], "explode");

                ctr--;
                if (addReelsToRight) {
                    reelId--;
                } else {
                    reelId++;
                }
            }

            if (!maxExpansion) {
                tl.add(Game.handleCornerRemoval(targetReelId, 0, targetReelId, (Game.ACTIVE_ROWS_NUM - 1)), "explode");
            }
        } else if (extraRows > 0) {
            let ctr: number = 3,
                rowId: number = targetRowId;

            while (ctr > -1) {
                for (let i: number = targetReelId - ctr; i < targetReelId; i++) {
                    tl.add(Game.explodeSymbols(i, rowId, rowId + 1, true, false), "explode");
                }
                for (let i: number = targetReelId; i <= targetReelId + ctr; i++) {
                    tl.add(Game.explodeSymbols(i, rowId, rowId + 1, true, false), "explode");
                }
                ctr--;
                if (addRowsInBottom) {
                    rowId--;
                } else {
                    rowId++
                }
            }

            if (!maxExpansion) {
                tl.add(Game.handleCornerRemoval(0, targetRowId, (Game.ACTIVE_REELS_NUM - 1), targetRowId), "explode");
            }
        }

        tl.add(() => {
            Game.shakeScreen()
        }, "explode");

        if (Game.canRemoveSymbol(bzSymbol)) {
            tl.add(Game.reelsView.reelModels[bazookaTarget.x].removeSymbol(bzSymbol), "explode");

            tl.add(() => {
                bzSymbol.isRemoved = true;
            });
        }

        return tl;
    }

    private onSkip(tl: gsap.core.Timeline): void {
        tl.totalProgress(1);
        Game.soundPlayer.stopSound(Sound.BZ_ACTIVATE);
        Game.soundPlayer.stopSound(Sound.BZ_EXPLODE);
    }

    private checkIfTriggeringMonsterExploding(bazookaTarget: IMonsterWalk, extraReels: number, extraRows: number, addReelsToRight: boolean, addRowsInBottom: boolean, monsterSymbolToCheck: string): boolean {
        const targetReelId: number = bazookaTarget.x,
            targetRowId: number = bazookaTarget.y;

        if (extraReels > 0) {
            let ctr: number = 3,
                reelId: number = targetReelId;

            while (ctr > -1) {
                if (Game.checkIfTriggeringMonsterIsPartOfExplosion(reelId, targetRowId - ctr, targetRowId + 1, monsterSymbolToCheck)) {
                    return true;
                } else if (Game.checkIfTriggeringMonsterIsPartOfExplosion(reelId, targetRowId, targetRowId + ctr + 1, monsterSymbolToCheck)) {
                    return true;
                }

                ctr--;
                if (addReelsToRight) {
                    reelId--;
                } else {
                    reelId++;
                }
            }
        } else if (extraRows > 0) {
            let ctr: number = 3,
                rowId: number = targetRowId;

            while (ctr > -1) {
                for (let i: number = targetReelId - ctr; i < targetReelId; i++) {
                    if (Game.checkIfTriggeringMonsterIsPartOfExplosion(i, rowId, rowId + 1, monsterSymbolToCheck)) {
                        return true;
                    }
                }
                for (let i: number = targetReelId; i <= targetReelId + ctr; i++) {
                    if (Game.checkIfTriggeringMonsterIsPartOfExplosion(i, rowId, rowId + 1, monsterSymbolToCheck)) {
                        return true;
                    }
                }
                ctr--;
                if (addRowsInBottom) {
                    rowId--;
                } else {
                    rowId++
                }
            }
        }

        return false;
    }

    private handleMonstersExpression(bazookaTarget: IMonsterWalk, addReelsToRight: boolean, addRowsInBottom: boolean, delay: number = 0): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            monstersToExplode: string[] = [],
            targetReelId: number = bazookaTarget.x,
            targetRowId: number = bazookaTarget.y,
            monsters: IMonster[] = Game.model.gameData.monsters,
            carriedSymbolUserNode: IPosition = Game.model.gameData.carriedSymbolUserNode;

        let attackingMonster: MonsterSymbol = Game.reelsView.reelModels[carriedSymbolUserNode.x].getMonsterSymbolAtRowId(carriedSymbolUserNode.y);

        for (let ctr: number = 0; ctr < monsters.length; ctr++) {
            const monster: IMonster = monsters[ctr];
            if (!monster.path || monster.path.length === 0) {
                continue;
            }

            if (monster.path[0].x === carriedSymbolUserNode.x && monster.path[0].y === carriedSymbolUserNode.y) {
                const monsterSymbol: MonsterSymbol = Game.reelsView.reelModels[monster.path[0].x].getMonsterSymbolAt(monster.symbol);
                attackingMonster = monsterSymbol;
                break;
            }
        }

        if (targetReelId === 0 || targetReelId === Game.ACTIVE_REELS_NUM - 1) {
            let ctr: number = 3,
                reelId: number = targetReelId;

            while (ctr > -1) {
                Game.getMonstersToExplode(reelId, targetRowId - ctr, targetRowId + 1, monstersToExplode);
                Game.getMonstersToExplode(reelId, targetRowId, targetRowId + ctr + 1, monstersToExplode);

                ctr--;
                if (addReelsToRight) {
                    reelId--;
                } else {
                    reelId++;
                }
            }
        } else if (targetRowId === 0 || targetRowId === Game.ACTIVE_ROWS_NUM - 1) {
            let ctr: number = 3,
                rowId: number = targetRowId;

            while (ctr > -1) {
                for (let i: number = targetReelId - ctr; i < targetReelId; i++) {
                    Game.getMonstersToExplode(i, rowId, rowId + 1, monstersToExplode);
                }
                for (let i: number = targetReelId; i <= targetReelId + ctr; i++) {
                    Game.getMonstersToExplode(i, rowId, rowId + 1, monstersToExplode);
                }

                ctr--;
                if (addRowsInBottom) {
                    rowId--;
                } else {
                    rowId++
                }
            }
        }

        Game.updateSurprisedMonsters(monstersToExplode, attackingMonster);

        Game.monsterReelsView.monsterSymbolContainer.children.forEach((monster: PIXI.DisplayObject) => {
            if (monstersToExplode.includes(monster.name)) {
                tl.add([
                    (monster as MonsterSymbol).getStateAnimation(MONSTER_EXPRESSION.SHOCK, 5),
                    () => {
                        if (monster.name !== attackingMonster.name){
                            Game.soundPlayer.playSound(monster.name + "Surprised" + MathHelper.randomIntInRange(1, 3), Game.getSurprisedSoundPlayVolume());
                        }
                    }
                ], 0.2);
            }
        });

        return tl;
    }
}